import { Field, useFormikContext } from "formik";
import React, { FC } from "react";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { VALIDITY_IN_OPTION } from "../../../models/CertificateType";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { startsWithZero } from "../../../utilities/regex";
import { requiredValidation, validateValidityInOption } from "../../../utilities/Validation";
import Label from "../../label/label";

interface CertificateValidityProps {
    onNext: (e: React.MouseEvent<HTMLButtonElement>) => void;
    setStepsDescription: React.Dispatch<React.SetStateAction<{ details: string; encryption: string; subjectAlternativeName: string }>>;
}

const CertificateValidity: FC<CertificateValidityProps> = ({ onNext, setStepsDescription }) => {
    enum ValidityFor {
        ADD = 'ADD',
        SUBTRACT = 'SUBTRACT'
    }

    const { translate } = useLocale();
    const validityInOptions: MultiSelectOption[] = [
        { value: VALIDITY_IN_OPTION.YEARS, label: translate.byKey(TranslationKeys.years) },
        { value: VALIDITY_IN_OPTION.DAYS, label: translate.byKey(TranslationKeys.days) }
    ];
    const { values, errors, setFieldValue } = useFormikContext<any>();

    const onValidityInChanged = (option: MultiSelectOption) => {
        setFieldValue("validityIn", option.value);
        setFieldValue("validityInLabel", option.label);
    }

    const handleValidityForChange = (type: ValidityFor) => {
        if (type === ValidityFor.ADD) {
            setFieldValue("validFor", Number(values.validFor) + 1);
        } else if (type === ValidityFor.SUBTRACT) {
            setFieldValue("validFor", Number(values.validFor) - 1);
        }
    }

    const handleNumericInputChange = (e: React.ChangeEvent<HTMLInputElement>, form: any) => {
        const inputValue = e.target.value;
        const numericValue = inputValue.replace(/[^0-9]/g, "");

        if (numericValue && !startsWithZero.test(numericValue)) {
            setFieldValue("validFor", numericValue);
        } else if (inputValue && (!numericValue || startsWithZero.test(numericValue))) {
            // Reset to previous valid value or default
            setFieldValue("validFor", "1");
        }
    }

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setStepsDescription((prev) => ({
            ...prev,
            details: values.alias ? values.alias : translate.byKey(TranslationKeys.pending),
        }));
        onNext(e);
    }

    const isValid = (): boolean => {
        return values.validityIn && values.validFor;
    }

    return <div className="px-3 h-full relative">
        <span className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`}>{translate.byKey(TranslationKeys.certificateValidity)}</span>

        <div className='flex flex-col space-y-2'>
            {/* Validity In */}
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="validityIn"
                        className="text-xs text-black mt-1"
                        required={true}>
                        {translate.byKey(TranslationKeys.validityIn)}:
                    </Label>
                    {errors.validityIn ? <span className='text-red-600 text-xs'>{errors.validityIn}</span> : null}
                </div>
                <div className='flex flex-row space-x-4'>
                    {validityInOptions.map((option) => (
                        <div
                            key={option.value}
                            className="flex items-center text-sm font-medium ">
                            <Field
                                id={`radio-${option.value}`}
                                type="radio"
                                name="validityIn"
                                value={option.value}
                                validate={(value) => validateValidityInOption(value, translate)}
                                onChange={(_: any) => onValidityInChanged(option)}
                            />
                            <Label htmlFor={`radio-${option.value}`} className="pl-1 text-sm">{option.label}</Label>
                        </div>
                    ))}
                </div>
            </div>
            {/* Valid for */}
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between" id='validFor'>
                    <Label htmlFor='validFor'
                        className="text-xs text-black mt-1 mb-1"
                        required>{translate.byKey(TranslationKeys.duration)}:</Label>
                    {errors.validFor ? <span className='text-red-600 text-xs'>{errors.validFor}</span> : null}
                </div>
                <div className='flex flex-row space-x-2 h-6'>
                    <button type={'button'}
                        onClick={() => handleValidityForChange(ValidityFor.SUBTRACT)}
                        disabled={values.validFor < 2}
                        className={`flex flex-row text-portal-blue-primary justify-center items-center rounded-md border border-solid border-gray-400 w-6 text-center pr-2 pl-2 p-1 text-sm bg-gray-input ${values.validFor < 2 ? 'cursor-not-allowed' : ''}`}>
                        -
                    </button>
                    <Field
                        name="validFor"
                        id="validityForInputFieldId"
                        inputMode="numeric"
                        pattern="[1-9][0-9]*"
                        className="flex flex-row items-center w-12 pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input text-center"
                        onChange={handleNumericInputChange}
                        validate={(value) => requiredValidation(value, translate)} />
                    <button type={'button'}
                        onClick={() => handleValidityForChange(ValidityFor.ADD)}
                        className="flex flex-row text-portal-blue-primary justify-center items-center rounded-md border border-solid border-gray-400 w-6 text-center pr-2 pl-2 p-1 text-sm bg-gray-input">
                        +
                    </button>
                </div>
            </div>
        </div>
        <div className="flex justify-end w-full absolute bottom-0 pr-3 pb-3 ">
            <button
                className={`py-1 px-5 mt-3 secondary ${!isValid() ? 'secondary-disabled cursor-not-allowed' : 'text-white bg-portal-blue-primary-shade2'}`}
                type="button"
                disabled={!isValid()}
                onClick={onLocalNext}>{translate.byKey(TranslationKeys.next)}</button>
        </div>
    </div>
}

export default CertificateValidity;